import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LifeBuoy } from "lucide-react";

import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { dashboardPathForRole } from "../lib/roleRoutes";
import { LoginForm } from "../components/auth/LoginForm";
import { RegisterForm } from "../components/auth/RegisterForm";
import { MfaChallenge } from "../components/auth/MfaChallenge";

const Auth = ({ mode = "login" }) => {
  const { register } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("familia");
  const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [accepted, setAccepted] = useState({ terms: false, lgpd: false });

  const isLogin = mode === "login";
  const from = location.state?.from?.pathname || null;

  const redirectByRole = (role) => {
    // Se o usuário veio de uma rota protegida (ex: tentou abrir /consultor sem
    // login), volta pra lá. Senão, manda direto pro painel do papel dele.
    const target = from && from !== "/" && from !== "/entrar" && from !== "/cadastro"
      ? from
      : dashboardPathForRole(role);
    window.location.href = target;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isLogin) {
        const { data } = await api.post("/auth/login", { email, password });
        if (data.requires_2fa) {
          setMfa({ token: data.mfa_token, user: data.user });
          setBusy(false);
          return;
        }
        if (data.requires_password_change || data.user?.requires_password_change) {
          toast.info("Defina uma senha pessoal para continuar.");
          window.location.href = "/trocar-senha";
          return;
        }
        toast.success("Bem-vindo(a) de volta");
        redirectByRole(data.user.role);
        return;
      }
      const user = await register({ name, email, password, role });
      toast.success("Conta criada com sucesso");
      // Família → passa pela vitrine de planos antes de cair no painel.
      // Outros papéis mantêm o fluxo antigo (painel próprio).
      if (user.role === "familia") {
        nav("/filho-presente/planos?welcome=1");
      } else {
        nav(dashboardPathForRole(user.role));
      }
    } catch (err) {
      // Backend pode retornar `detail` como string (401/400) ou como lista de
      // erros de validação (422). Tratamos os dois para não crashar a UI.
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d
        : Array.isArray(d) ? (d[0]?.msg || "Dados inválidos")
        : "Erro na autenticação";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login/2fa", { mfa_token: mfa.token, code: mfaCode });
      toast.success("Autenticado com 2FA");
      redirectByRole(data.user.role);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Código inválido");
    } finally { setBusy(false); }
  };

  const startGoogle = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-8rem)] grid md:grid-cols-2">
      <div className="hidden md:block relative overflow-hidden bg-[#0B2B62]">
        {/* Camadas de fundo — sem foto pra manter tom sólido e limpo */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B2B62] via-[#0B2B62] to-[#08204A]" />
        {/* Grid sutil */}
        <div className="absolute inset-0 opacity-[0.08]"
             style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
        {/* Halo âmbar no canto — quente + convidativo */}
        <div className="absolute -bottom-40 -left-32 w-[520px] h-[520px] rounded-full"
             style={{ background: "radial-gradient(closest-side, rgba(251,191,36,.18), rgba(251,191,36,0) 70%)" }} />
        <div className="absolute -top-32 -right-24 w-[380px] h-[380px] rounded-full"
             style={{ background: "radial-gradient(closest-side, rgba(96,165,250,.16), rgba(96,165,250,0) 70%)" }} />

        <div className="relative p-12 h-full flex flex-col justify-between text-white">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-md bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center">
              <LifeBuoy className="w-5 h-5" strokeWidth={1.8} />
            </div>
            <div>
              <div className="font-display font-bold text-xl tracking-tight">RAF Emergência</div>
              <div className="text-[11px] uppercase tracking-widest text-white/70">Acolhimento · Direcionamento</div>
            </div>
          </div>
          <div>
            <div className="inline-block text-[10px] uppercase tracking-[0.22em] text-amber-300/90 font-semibold mb-4 bg-amber-400/10 border border-amber-300/25 rounded-full px-3 py-1">
              Do primeiro contato à admissão
            </div>
            <h2 className="font-display font-bold text-4xl leading-[1.1] max-w-md tracking-tight">
              Cada família merece <span className="text-amber-300">clareza</span> em cada etapa.
            </h2>
            <p className="mt-4 text-white/75 max-w-md text-sm leading-relaxed">
              Triagem inteligente, dossiê estruturado e consultor humano — em um único fluxo seguro, 24h por dia.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              <div>
                <div className="font-display font-bold text-2xl text-amber-300">24h</div>
                <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">Atendimento contínuo</div>
              </div>
              <div>
                <div className="font-display font-bold text-2xl text-white">LGPD</div>
                <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">Dados criptografados</div>
              </div>
              <div>
                <div className="font-display font-bold text-2xl text-white">100%</div>
                <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">Humano no ciclo</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        {mfa ? (
          <MfaChallenge
            mfa={mfa} mfaCode={mfaCode} setMfaCode={setMfaCode} busy={busy}
            onSubmit={submit2fa} onBack={() => { setMfa(null); setMfaCode(""); }}
          />
        ) : isLogin ? (
          <LoginForm
            email={email} setEmail={setEmail}
            password={password} setPassword={setPassword}
            busy={busy} onSubmit={submit} onGoogle={startGoogle}
          />
        ) : (
          <RegisterForm
            name={name} setName={setName}
            email={email} setEmail={setEmail}
            password={password} setPassword={setPassword}
            role={role} setRole={setRole}
            busy={busy} onSubmit={submit} onGoogle={startGoogle}
            accepted={accepted} setAccepted={setAccepted}
          />
        )}
      </div>
    </div>
  );
};

export default Auth;
